import React, { useEffect, useState } from "react";
import useDefaultFilter from "./useDefaultFilter";
import {
  DateInput,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  useDataProvider,
  useGetOne,
} from "react-admin";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { Close as CloseIcon } from "@mui/icons-material";
import { Button, IconButton, Drawer } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Timestamp } from "firebase/firestore";
import {
  renderTransactionStatus,
  transactionStatus,
} from "../Employee/ValidateCSV";
import axios from "axios";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  form: {
    alignSelf: "flex-end",
    paddingBottom: 8,
  },
  formContent: {
    padding: theme.spacing(3),
  },
  formContentButtons: {
    display: "flex",
    marginTop: theme.spacing(2),
  },
  closeButton: {
    marginTop: 5,
    marginLeft: 5,
  },
  searchBox: {
    display: "flex",
    alignItems: "baseline",
  },
  searchInput: {
    padding: 15,
  },
  searchButton: {
    marginLeft: 5,
  },
  drawer: {
    width: 420,
    maxWidth: "100%",
    paddingLeft: "1rem",
    paddingRight: "1rem",
  },
}));

const formatDate = (date, startOfDay = true) => {
  if (!date) return null;
  const d = new Date(date);
  return startOfDay
    ? new Date(d.setHours(0, 0, 0, 0)).getTime()
    : new Date(d.setHours(23, 59, 59, 999)).getTime();
};

const defaultFilterValues = {
  machineId: "",
  dateStart: "",
  dateEnd: "",
  status: "2",
};

const getStoredFilterValues = (storageKey) => {
  if (!storageKey) return { ...defaultFilterValues };

  const savedFilters = sessionStorage.getItem(storageKey);
  return savedFilters
    ? { ...defaultFilterValues, ...JSON.parse(savedFilters) }
    : { ...defaultFilterValues };
};

const TransactionFilter = ({
  setFilteredData,
  allData,
  onModalOpenChange,
  modalOpen,
  setAppliedFilters,
  setTotalAmount,
  setTotal,
  setPage,
  setFilter,
  setIsLoading,
  sales = false,
  storageKey,
  mode = "commercial",
  clientId,
}) => {
  const dataProvider = useDataProvider();

  const [role, setRole] = useState(null);
  const [filters, setFilterValues] = useState(() =>
    getStoredFilterValues(storageKey),
  );
  const statusChoices = [
    { id: "1", name: "Success" },
    { id: "0", name: "Failed" },
    { id: "-1", name: "Canceled" },
    { id: "-2", name: "Refunded" },
    { id: "-3", name: "Void" },
    { id: "-4", name: "DispensingEr" },
    { id: "2", name: "ALL" },
  ];

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await getCurrentUser(user.uid);
        } catch (err) {
          console.error("Error in authentication or fetching data:", err);
        }
      } else {
        setRole(null);
      }
    });

    return () => checkAuth();
  }, [dataProvider]);

  const getCurrentUser = async (userId) => {
    try {
      const res = await dataProvider.getOne("users", { id: userId });
      setRole(res.data.role);
    } catch (err) {
      console.log(err);
      setRole(null);
    }
  };

  const parseFormDataToFilters = (data) => {
    let filters = role === "client" ? { status: 1 } : {};
    const { machineId, dateStart, dateEnd, status } = data;

    if (machineId) {
      filters["machine.id"] = machineId;
    }

    if (dateStart || dateEnd) {
      const date = [];
      if (dateStart) {
        date.push({
          operator: ">=",
          value: formatDate(dateStart, true),
          isDate: true,
        });
      }
      if (dateEnd) {
        date.push({
          operator: "<=",
          value: formatDate(dateEnd, false),
          isDate: true,
        });
      }
      filters.date = date;
    }

    if (status !== undefined && status !== null) {
      const statusInNumber = parseInt(status, 10);
      if (statusInNumber >= -3 && statusInNumber <= 2) {
        filters.status = statusInNumber;
      }
    }

    return filters;
  };

  const parseFiltersToFormData = (filters) => {
    const formData = {};

    if (filters?.["machine.id"]) {
      formData.machineId = filters["machine.id"];
    }

    if (filters?.date) {
      filters.date.forEach(({ operator, value }) => {
        if (operator === ">=") {
          formData.dateStart = new Date(value).toISOString().split("T")[0];
        } else if (operator === "<=") {
          formData.dateEnd = new Date(value).toISOString().split("T")[0];
        }
      });
    }

    if (filters?.status !== undefined) {
      formData.status = filters.status.toString();
    }

    return formData;
  };

  //machine input filter
  const machineInputFilter = {
    ...useDefaultFilter("client.id"),
    isDeleted: false,
  };

  const handleChange = (e) => {
    if (!e?.target?.value) return;
    const { name, value } = e.target;
    setFilterValues((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = async () => {
    const start = filters.dateStart;
    const end = filters.dateEnd;

    if (start && end && !isDateRangeWithin3Months(start, end)) {
      toast.error("Date range cannot exceed 3 months.");
      return;
    }

    if (storageKey) {
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    }

    handleClose();

    if (sales && sales.module === "transaction") {
      setIsLoading?.(true);
      let totalPrice = 0;
      let token = localStorage.getItem("token");
      try {
        let selectedMachineMode = mode;
        if (filters.machineId) {
          const machine = await dataProvider.getOne("machines", {
            id: filters.machineId,
          });
          selectedMachineMode = machine?.data?.machineMode;
        }

        let params = {
          ...filters,
          skip: 0,
          limit: 25,
          mode: selectedMachineMode || "commercial",
        };
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/salesList`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const result = res.data.result.data;
        const total = res.data.result.total;

        totalPrice = result.reduce(
          (sum, item) => sum + (parseFloat(item.Amount) || 0),
          0,
        );

        setPage?.(1);
        setFilter?.(params);
        setTotal(
          typeof total === "number"
            ? total
            : Array.isArray(total)
              ? total.length
              : 0,
        );
        setFilteredData(result);
        setTotalAmount(totalPrice);

        //show apply tag
        const applied = [];
        if (filters.machineId) {
          const machine = await dataProvider.getOne("machines", {
            id: filters.machineId,
          });
          if (machine?.data?.machineUUID) {
            applied.push({
              key: "Machine UUID",
              value: machine.data.machineUUID,
            });
          }
        }

        if (filters.dateStart || filters.dateEnd) {
          applied.push({
            key: "Date",
            value: `${filters.dateStart} - ${filters.dateEnd}`,
          });
        }

        if (filters.status && filters.status !== "2") {
          const statusText = transactionStatus({
            status: parseInt(filters.status, 10),
          });
          applied.push({ key: "Status", value: statusText });
        }
        setAppliedFilters(applied);
        handleClose();
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading?.(false);
      }
    } else if (sales && sales.module === "order") {
      try {
        setIsLoading?.(true);
        let token = localStorage.getItem("token");
        let params = {
          ...filters,
          skip: 0,
          limit: 25,
          mode: "commercial",
        };
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/orderList`,
          {
            params,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const result = res.data.result.data;
        setFilteredData(result);
        handleClose();

        //show apply tag
        const applied = [];
        if (filters.machineId) {
          const machine = await dataProvider.getOne("machines", {
            id: filters.machineId,
          });
          if (machine?.data?.machineUUID) {
            applied.push({
              key: "Machine UUID",
              value: machine.data.machineUUID,
            });
          }
        }

        if (filters.dateStart || filters.dateEnd) {
          applied.push({
            key: "Date",
            value: `${filters.dateStart} - ${filters.dateEnd}`,
          });
        }

        if (filters.status && filters.status !== "2") {
          const statusText = transactionStatus({
            status: parseInt(filters.status, 10),
          });
          applied.push({ key: "Status", value: statusText });
        }

        setAppliedFilters(applied);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading?.(false);
      }
    } else {
      if (sales.module === "product") {
        setIsLoading?.(true);
        try {
          const token = localStorage.getItem("token");

          let params = {
            ...filters,
            sortField: "Date",
            sortOrder: "DESC",
            mode: "mro",
          };

          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByProduct`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );

          const result = res.data.result.data;
          const total = res.data.result.total;

          const grouped = result.reduce((acc, transaction, index) => {
            const productId = transaction.product?.id;
            if (!productId) return acc;

            if (!acc[productId]) {
              acc[productId] = {
                product: transaction.product,
                client: transaction.client,
                date: transaction.date,
                transactionId: transaction.transactionId,
                companyName: transaction.companyName,
                totalQty: 0,
                totalCredit: 0,
              };
            }

            const qty = Number(transaction.totalQty) || 0;
            const revenue = Number(transaction.totalRevenue) || 0;

            acc[productId].totalQty += qty;
            acc[productId].totalCredit += revenue;

            return acc;
          }, {});

          const groupedArray = Object.values(grouped).map((item) => ({
            ...item,
            totalCredit: Number(item.totalCredit.toFixed(2)),
          }));

          setPage?.(1);
          setFilter?.(params);
          setFilteredData(groupedArray);
          setTotal(
            typeof total === "number"
              ? total
              : Array.isArray(total)
                ? total.length
                : 0,
          );

          //show apply tag
          const applied = [];
          if (filters.machineId) {
            const machine = await dataProvider.getOne("machines", {
              id: filters.machineId,
            });
            if (machine?.data?.machineUUID) {
              applied.push({
                key: "Machine UUID",
                value: machine.data.machineUUID,
              });
            }
          }

          if (filters.dateStart || filters.dateEnd) {
            applied.push({
              key: "Date",
              value: `${filters.dateStart} - ${filters.dateEnd}`,
            });
          }

          if (filters.status && filters.status !== "2") {
            const statusText = transactionStatus({
              status: parseInt(filters.status, 10),
            });
            applied.push({ key: "Status", value: statusText });
          }

          setAppliedFilters?.(applied);
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading?.(false);
          handleClose();
        }
      } else if (sales.module === "employee") {
        try {
          setIsLoading?.(true);
          const token = localStorage.getItem("token");

          let params = {
            ...filters,
            skip: 0,
            limit: 2000,
            sortField: "Date",
            sortOrder: "DESC",
            mode: "mro",
          };

          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByEmployee`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );

          const result = res.data.result.data;
          const total = res.data.result.total;

          const grouped = result.reduce((acc, transaction) => {
            const employeeName = transaction.employee?.fullName;
            if (!employeeName) return acc;

            if (!acc[employeeName]) {
              acc[employeeName] = {
                employee: transaction.employee,
                client: transaction.client,
                date: transaction.date,
                transactionId: transaction.transactionId,
                companyName: transaction.companyName,
                totalQty: 0,
                totalTransaction: 0,
                totalCredit: 0,
              };
            }

            const qty = Number(transaction.totalQty) || 0;
            const totalTransaction = Number(transaction.totalTransaction) || 0;
            const revenue = Number(transaction.totalRevenue) || 0;

            acc[employeeName].totalQty += qty;
            acc[employeeName].totalTransaction += totalTransaction;
            acc[employeeName].totalCredit += revenue;

            return acc;
          }, {});

          const groupedArray = Object.values(grouped).map((item) => ({
            ...item,
            totalCredit: Number(item.totalCredit.toFixed(2)),
          }));

          setPage?.(1);
          setFilter?.(params);
          setFilteredData(groupedArray);
          setTotal(
            typeof total === "number"
              ? total
              : Array.isArray(total)
                ? total.length
                : 0,
          );

          //show apply tag
          const applied = [];
          if (filters.machineId) {
            const machine = await dataProvider.getOne("machines", {
              id: filters.machineId,
            });
            if (machine?.data?.machineUUID) {
              applied.push({
                key: "Machine UUID",
                value: machine.data.machineUUID,
              });
            }
          }

          if (filters.dateStart || filters.dateEnd) {
            applied.push({
              key: "Date",
              value: `${filters.dateStart} - ${filters.dateEnd}`,
            });
          }

          if (filters.status && filters.status !== "2") {
            const statusText = transactionStatus({
              status: parseInt(filters.status, 10),
            });
            applied.push({ key: "Status", value: statusText });
          }

          setAppliedFilters?.(applied);
          handleClose();
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading?.(false);
        }
      } else {
        try {
          setIsLoading?.(true);
          const token = localStorage.getItem("token");
          let totalPrice = 0;

          let params = {
            ...filters,
            skip: 0,
            limit: 250, // or whatever perPage value you're using
            sortField: "Date",
            sortOrder: "DESC",
            mode: "mro",
          };

          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroList`,
            {
              params,
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            },
          );

          const result = res.data.result.data;

          setPage?.(1);
          setFilter?.(params);

          totalPrice = result.reduce(
            (sum, item) => sum + (parseFloat(item.Amount) || 0),
            0,
          );
          setFilteredData(result);
          setTotalAmount?.(totalPrice);
          setTotal(res.data.result.total);

          //show apply tag
          const applied = [];
          if (filters.machineId) {
            const machine = await dataProvider.getOne("machines", {
              id: filters.machineId,
            });
            if (machine?.data?.machineUUID) {
              applied.push({
                key: "Machine UUID",
                value: machine.data.machineUUID,
              });
            }
          }

          if (filters.dateStart || filters.dateEnd) {
            applied.push({
              key: "Date",
              value: `${filters.dateStart} - ${filters.dateEnd}`,
            });
          }

          if (filters.status && filters.status !== "2") {
            const statusText = transactionStatus({
              status: parseInt(filters.status, 10),
            });
            applied.push({ key: "Status", value: statusText });
          }

          setAppliedFilters?.(applied);
          handleClose();
        } catch (error) {
          console.log(error);
        } finally {
          setIsLoading?.(false);
        }
      }
    }
  };

  const clearFilters = async () => {
    if (storageKey) {
      sessionStorage.removeItem(storageKey);
    }
    setFilterValues({ ...defaultFilterValues });
    setAppliedFilters?.([]);
    setPage?.(1);

    try {
      const token = localStorage.getItem("token");

      const commonParams = {
        skip: 0,
        limit: 25,
        sortField: "Date",
        sortOrder: "DESC",
      };

      let url = "";
      const params = { ...commonParams };

      if (sales && sales.module === "transaction") {
        url = `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/salesList`;
        params.mode = "commercial";
      } else if (sales && sales.module === "order") {
        url = `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/orderList`;
        params.mode = "commercial";
        if (clientId) params.client = clientId;
      } else {
        if (sales.module === "product") {
          console.log("in");
          url = `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByProduct`;
          params.mode = "mro";
        } else if (sales.module === "employee") {
          url = `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroListByEmployee`;
          params.mode = "mro";
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/machine_transaction/mroList`;
          params.mode = "mro";
        }
      }

      const res = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = res.data.result.data;
      const total = res.data.result.total;

      let groupedArray = result;

      if (sales?.module === "product") {
        const grouped = result.reduce((acc, transaction) => {
          const productName = transaction.product?.name;
          if (!productName) return acc;

          if (!acc[productName]) {
            acc[productName] = {
              product: transaction.product,
              client: transaction.client,
              date: transaction.date,
              transactionId: transaction.transactionId,
              companyName: transaction.companyName,
              totalQty: 0,
              totalCredit: 0,
            };
          }

          const qty = Number(transaction.totalQty) || 0;
          const revenue = Number(transaction.totalRevenue) || 0;

          acc[productName].totalQty += qty;
          acc[productName].totalCredit += revenue;

          return acc;
        }, {});

        groupedArray = Object.values(grouped);
      } else if (sales?.module === "employee") {
        const grouped = result.reduce((acc, transaction) => {
          const employeeName = transaction.employee?.fullName;
          if (!employeeName) return acc;

          if (!acc[employeeName]) {
            acc[employeeName] = {
              employee: transaction.employee,
              client: transaction.client,
              date: transaction.date,
              transactionId: transaction.transactionId,
              companyName: transaction.companyName,
              totalQty: 0,
              totalCredit: 0,
            };
          }

          const qty = Number(transaction.totalTransaction) || 0;
          const revenue = Number(transaction.totalRevenue) || 0;

          acc[employeeName].totalQty += qty;
          acc[employeeName].totalCredit += revenue;

          return acc;
        }, {});

        groupedArray = Object.values(grouped);
      }

      const totalPrice = result.reduce(
        (sum, item) => sum + (parseFloat(item.Amount) || 0),
        0,
      );

      setFilteredData(groupedArray);
      setTotalAmount?.(totalPrice);
      setTotal(
        typeof total === "number"
          ? total
          : Array.isArray(total)
            ? total.length
            : 0,
      );
    } catch (error) {
      console.error("Failed to reload full data after clear:", error);
    }

    onModalOpenChange?.(false);
  };

  const handleClose = () => {
    onModalOpenChange?.(false);
  };

  function isDateRangeWithin3Months(start, end, maxDays = 92) {
    if (!start || !end) return true;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffInDays =
      Math.abs(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays <= maxDays;
  }
  return (
    <>
      <Drawer
        anchor="right"
        open={modalOpen}
        onClose={handleClose}
        PaperProps={{
          style: {
            transform: "none",
            display: "block",
            width: "420px",
            maxWidth: "100%",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <SimpleForm toolbar={null} defaultValues={filters}>
          <ReferenceInput
            label="Machine"
            source="machineId"
            reference="machines"
            filter={machineInputFilter}
            filterToQuery={() => {}}
            sort={{ field: "machineUUID", order: "DESC" }}
            perPage={9999}
            allowEmpty={false}
            name="machineId"
          >
            <SelectInput
              fullWidth
              value={filters.machineId}
              onChange={handleChange}
              optionText="machineUUID"
              emptyText="Show all machines"
              margin="normal"
            />
          </ReferenceInput>
          <DateInput
            label="Start Date"
            type="date"
            name="dateStart"
            value={filters.dateStart}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            margin="normal"
          />
          <DateInput
            label="End Date"
            type="date"
            name="dateEnd"
            value={filters.dateEnd}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
            margin="normal"
          />
          <SelectInput
            label="Status"
            source="status"
            value={filters.status}
            choices={[
              { id: "1", name: "Success" },
              { id: "0", name: "Failed" },
              { id: "-1", name: "Canceled" },
              { id: "-2", name: "Refunded" },
              { id: "-3", name: "Void" },
              { id: "2", name: "ALL" },
            ]}
            onChange={handleChange}
            defaultValue="2"
            fullWidth
            margin="normal"
            emptyText={null}
          />
        </SimpleForm>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 20,
            marginBottom: 20,
            paddingRight: 16,
            paddingLeft: 16,
          }}
        >
          <Button variant="contained" color="primary" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear
          </Button>
        </div>
      </Drawer>
    </>
  );
};

export default TransactionFilter;
